import {
  Checkbox,
  checkboxStates,
  GenericTable,
  GenericTableVariant,
  TooltipTrigger,
} from "@visiblethread/react-components";
import Ellipsis from "components/Ellipsis";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  getAllDocuments,
  getFolderNode,
} from "slices/workspace/navigationNodes";

const isChecked = (doc, baselineDoc, latestDoc) => {
  return doc === baselineDoc || doc === latestDoc;
};

const getFolderPath = (folder, workspace) => {
  let path = folder.text;
  const { navigationInfo } = workspace;
  let parentFolder = getFolderNode(navigationInfo, folder.parentId);
  while (parentFolder) {
    path = `${parentFolder.text} / ${path}`;
    parentFolder = getFolderNode(navigationInfo, parentFolder.parentId);
  }
  return path;
};

const getColumns = (baselineDoc, latestDoc, workspace, handleDocCheck) => [
  {
    header: "Name",
    accessorKey: "text",
    id: "name",
    size: 267,
    minSize: 100,
    cell: ({ row }) => {
      const doc = row.original;
      return <Ellipsis text={doc.text} />;
    },
    sortingFn: (a, b) => a.original.text.localeCompare(b.original.text),
  },
  {
    header: "Folder",
    accessorKey: "text",
    id: "folder",
    size: 267,
    minSize: 100,
    cell: ({ row }) => {
      const doc = row.original;
      const folder = getFolderNode(workspace.navigationInfo, doc.folderId);
      const folderName = folder.text;
      const folderPath = getFolderPath(folder, workspace);
      return (
        <TooltipTrigger
          content={folderPath}
          delay={100}
          style={{ overflow: "auto" }}
        >
          <div className="folder_name" data-clarity-mask="True">
            {folderName}
          </div>
        </TooltipTrigger>
      );
    },
    sortingFn: (a, b) => {
      const { navigationInfo } = workspace;
      const aFolder = getFolderNode(navigationInfo, a.original.parentId);
      const bFolder = getFolderNode(navigationInfo, b.original.parentId);
      return aFolder.text.localeCompare(bFolder.text);
    },
  },
  {
    header: "",
    accessorKey: "",
    id: "checked",
    size: 40,
    minSize: 40,
    cell: ({ row }) => {
      const doc = row.original;
      const checked = isChecked(doc, baselineDoc, latestDoc);

      return (
        <div
          style={{ display: "flex", justifyContent: "center" }}
          data-clarity-mask="True"
        >
          <Checkbox
            state={checked ? checkboxStates.checked : checkboxStates.empty}
            handleChange={() => handleDocCheck(doc)}
            ariaLabel={doc.name}
          />
        </div>
      );
    },
    enableSorting: false,
  },
];

const getData = (workspace, allDocsInWorkspace, folderId, excel) => {
  const entries = getAllDocuments(workspace.navigationInfo);
  return entries.filter((entry) => {
    if (!entry.isDoc) {
      return false;
    }
    if (!allDocsInWorkspace && entry.folderId !== folderId) {
      return false;
    }
    if (excel && entry.docType !== "excel") {
      return false;
    }
    return !(!excel && entry.docType !== "word" && entry.docType !== "pdf");
  });
};

const DocumentsTable = ({
  allDocsInWorkspace,
  folderId,
  excel,
  checkedDocByDefault,
  onChange,
}) => {
  const workspace = useSelector((state) => state.workspace);
  const data = getData(workspace, allDocsInWorkspace, folderId, excel);
  const [baselineDoc, setBaselineDoc] = useState(checkedDocByDefault);
  const [latestDoc, setLatestDoc] = useState();

  useEffect(() => {
    onChange(baselineDoc, latestDoc);
  }, [baselineDoc, latestDoc, onChange]);

  useEffect(() => {
    setBaselineDoc(checkedDocByDefault ? checkedDocByDefault : null);
    setLatestDoc(null);
  }, [allDocsInWorkspace, checkedDocByDefault]);

  const handleDocCheck = (doc) => {
    const checked = isChecked(doc, baselineDoc, latestDoc);

    if (!checked) {
      if (!baselineDoc) {
        setBaselineDoc(doc);
      } else if (!latestDoc) {
        setLatestDoc(doc);
      } else {
        return;
      }
    } else {
      if (doc === baselineDoc) {
        if (latestDoc) {
          setBaselineDoc(latestDoc);
          setLatestDoc(null);
        } else {
          setBaselineDoc(null);
        }
      } else if (doc === latestDoc) {
        setLatestDoc(null);
      } else {
        return;
      }
    }
  };

  const selectedRowIds = [baselineDoc?.vtid, latestDoc?.vtid].filter(
    (vtid) => vtid != null
  );

  return (
    <GenericTable
      columns={getColumns(baselineDoc, latestDoc, workspace, handleDocCheck)}
      data={data}
      defaultSortColumnIndex={1}
      onRowClick={handleDocCheck}
      enableRowSelection
      rowIdKey="vtid"
      selectedRowIds={selectedRowIds}
      selectedRowClassName="compare_documents_modal__selected_row"
      variant={GenericTableVariant.bordered}
    />
  );
};

DocumentsTable.propTypes = {
  allDocsInWorkspace: PropTypes.bool.isRequired,
  folderId: PropTypes.number.isRequired,
  excel: PropTypes.bool.isRequired,
  checkedDocByDefault: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default DocumentsTable;
